import {Link, Outlet, useLocation} from 'react-router-dom';
import { Button, Divider, Drawer, Layout, Space } from 'antd';
import React, {useEffect, useMemo, useState} from 'react';
import { MenuOutlined, SettingOutlined } from '@ant-design/icons';
import { menuItems } from "../../utils/utils";
import '../../assets/css/MobileLayout.css';
import { DentobookApplicationContext } from "../../context/DentobookApplicationContext";
import { FormattedMessage } from "react-intl";
import RightHeaderMenu from "./RightHeaderMenu";
import { AlertContext } from '../../context/AlertContext';
import { HelpSectionContext } from '../../context/HelpSectionContext';
import { bitwiseOr, isAccessibleFor, isNotAccessibleFor } from "../../utils/accessUtils";
import { ALERT_DURATIONS } from "../../constants/alerts";
import CustomAlert from "./CustomAlert";
import DentobookLink from '../common/DentobookLink';

export const MobileLayout = () => {
    const { principal, userOrganization } = React.useContext(DentobookApplicationContext);

    const [visible, setVisible] = useState(false);
    const [warning, setWarning] = useState<React.ReactNode>('');
    const [error, setError] = useState<React.ReactNode>('');
    const [successMessage, setSuccessMessage] = useState<React.ReactNode>('');
    const [redirectUrl, setRedirectUrl] = useState<string | null>(null);


    const [open, setOpen] = useState<boolean>(false);
    const [selectedMenuItem, setSelectedMenuItem] = useState<string>('');
    const [accessibleMenuItems, setAccessibleMenuItems] = useState<any[]>([]);
    const location = useLocation();

    const showDrawer = () => {
        setOpen(true);
    };

    const closeDrawer = (path: string) => {
        setOpen(false);
        if (path) {
            setSelectedMenuItem(path);
        }
    };

    // Show warning banner if the interceptor switched orgs after membership revocation.
    useEffect(() => {
        const flash = sessionStorage.getItem('dentobook_session_event');
        if (!flash) return;
        try {
            const event = JSON.parse(flash);
            sessionStorage.removeItem('dentobook_session_event');
            if (event.type === 'org_switched_after_revoke') {
                setWarning(<FormattedMessage id="org_switched_after_revoke" values={{ orgName: event.orgName }} />);
                setTimeout(() => setWarning(''), ALERT_DURATIONS.STANDARD);
            }
        } catch { /* ignore malformed flash */ }
    }, []);

    useEffect(() => {
        const currentPath = location.pathname;
        const matchedPath = currentPath.includes('/patients/create') ? '/patients' : currentPath;
        setSelectedMenuItem(matchedPath);
        const accessibleMenuItems = menuItems.filter(item => {
            if ((item.name === 'nzis' && userOrganization?.country !== 'BG')
                || (item.name === 'activity_log' && !principal?.features.includes('ACTIVITY_LOG'))
                || (item.name === 'reports_label' && !principal?.features.includes('REPORTING'))) {
                return false;
            }
            if (item.accessibleFor) {
                return isAccessibleFor(principal?.user, bitwiseOr(item.accessibleFor));
            }
            if (item.notAccessibleFor) {
                return isNotAccessibleFor(principal?.user, bitwiseOr(item.notAccessibleFor));
            }
            return true;
        });
        setAccessibleMenuItems(accessibleMenuItems);
    }, [location.pathname, principal, userOrganization]);

    const alertContextValue = useMemo(() => ({
        visible,
        setVisible,
        warning,
        setWarning,
        error,
        setError,
        successMessage,
        setSuccessMessage,
        redirectUrl,
        setRedirectUrl
    }), [visible, warning, error, successMessage, redirectUrl]);

    const [helpSlug, setHelpSlug] = useState<string | null>(null);
    const helpContextValue = useMemo(() => ({ helpSlug, setHelpSlug }), [helpSlug]);

    return (
        <AlertContext.Provider value={alertContextValue} /*NOSONAR*/ >
            <HelpSectionContext.Provider value={helpContextValue}>
            <Layout>
                <CustomAlert />
                <nav className="nav">
                    {principal?.user ?
                        <Button shape="circle" className="button" onClick={showDrawer}>
                            <MenuOutlined />
                        </Button>
                        : <div>
                            <Link to="/">
                                <img src={require('../../assets/logo.png')} alt="Home" style={{ height: 40, cursor: 'pointer' }} />
                            </Link>
                        </div>
                    }
                    <div>
                        <RightHeaderMenu />
                    </div>
                </nav>

                {principal?.user ?
                    <Drawer
                        title={
                            <Link to="/">
                                <img src={require('../../assets/logo.png')} alt="Home" height={40} style={{ cursor: 'pointer' }} />
                            </Link>
                        }
                        placement="left"
                        closable={false}
                        onClose={() => closeDrawer("")}
                        open={open}
                        rootClassName="mobile-nav-drawer"
                        className="menu-layout"
                        key="drawer"
                        extra={
                            <Space>
                                <Button shape="circle" className="close-button" onClick={() => closeDrawer("")}>
                                    <MenuOutlined />
                                </Button>
                            </Space>
                        }
                    >
                        {accessibleMenuItems.map(m => {
                            const cssClassOne = selectedMenuItem === m.path ? 'menu-item menu-item-selected' : 'menu-item';
                            const cssClassTwo = m.bottom ? 'bottom-menu' : cssClassOne;

                            return <>
                                {m.bottom ? <Divider className="bottom-menu" /> : null}
                                <DentobookLink
                                    cssClassName={cssClassTwo}
                                    to={m.path} cb={() => closeDrawer(m.path)}>
                                    {m.bottom ? <SettingOutlined className="settings-icon" /> : null}
                                    <span>
                                        {m.name ? <FormattedMessage id={m.name} /> : m.path}
                                    </span>
                                </DentobookLink>
                            </>
                        })}
                    </Drawer>
                    : null}
                <Layout>
                    <div>
                        <Outlet />
                    </div>
                </Layout>
            </Layout>
            </HelpSectionContext.Provider>
        </AlertContext.Provider>
    )
};